import { lazy, Suspense, useEffect, useState } from 'react'
import { Analytics } from '@vercel/analytics/react'
import { ThemeProvider } from './context/ThemeContext'
import { Nav } from './components/Nav'
import { Hero } from './sections/Hero'
import { Features } from './sections/Features'
import { Roadmap } from './sections/Roadmap'
import { Footer } from './sections/Footer'

// Sub-routes are entered via hash overlays — keep them out of the home bundle.
const BetaPage = lazy(() => import('./pages/BetaPage').then(m => ({ default: m.BetaPage })))
const PrivacyPage = lazy(() => import('./pages/PrivacyPage').then(m => ({ default: m.PrivacyPage })))
const TermsPage = lazy(() => import('./pages/TermsPage').then(m => ({ default: m.TermsPage })))

/** Tiny hash-aware switch — no router needed for one extra page. */
function useHashRoute() {
  const read = () => (typeof window === 'undefined' ? '' : window.location.hash.replace(/^#/, ''))
  const [hash, setHash] = useState<string>(read)
  useEffect(() => {
    const onHash = () => setHash(read())
    window.addEventListener('hashchange', onHash)
    return () => window.removeEventListener('hashchange', onHash)
  }, [])
  return hash
}

export function App() {
  const hash = useHashRoute()

  // Privacy + Terms are content-heavy long-form pages — keep as real routes.
  if (hash === 'privacy') {
    return (
      <ThemeProvider>
        <Suspense fallback={<div className="min-h-screen bg-bg-0" />}>
          <PrivacyPage />
        </Suspense>
        <Analytics />
      </ThemeProvider>
    )
  }

  if (hash === 'terms') {
    return (
      <ThemeProvider>
        <Suspense fallback={<div className="min-h-screen bg-bg-0" />}>
          <TermsPage />
        </Suspense>
        <Analytics />
      </ThemeProvider>
    )
  }

  // Home (always rendered; /#beta opens BetaPage as an overlay on top).
  return (
    <ThemeProvider>
      <div className="relative min-h-screen bg-bg-0 text-ink-1 overflow-hidden">
        <a href="#main" className="skip-link">Skip to main content</a>
        <div
          aria-hidden
          className="pointer-events-none fixed inset-0 -z-10 opacity-70"
          style={{
            background:
              'radial-gradient(60% 40% at 50% 0%, var(--accent-soft) 0%, transparent 60%), radial-gradient(40% 30% at 90% 80%, var(--accent-soft) 0%, transparent 60%)',
          }}
        />
        <Nav />
        <main id="main">
          <Hero />
          <Features />
          {/* <Showcase /> hidden for now */}
          <Roadmap />
        </main>
        <Footer />
      </div>
      {hash === 'beta' && (
        <Suspense fallback={null}>
          <BetaPage />
        </Suspense>
      )}
      <Analytics />
    </ThemeProvider>
  )
}
